/**
 * One icon per idea, the same everywhere (lib/iconTable.ts is the table, as
 * data; tests/iconRegistry.test.ts holds the two together).
 *
 * Draw an idea through its name here — `<SigningIcon />`, `icon: DepositIcon` —
 * never by importing the lucide glyph directly: in the sales, contracts,
 * worksheet, deposit, signing and identity code the test refuses a direct
 * import of an icon that has an entry here. Change an idea's icon by changing
 * its row in the table and its line here, and every surface follows.
 *
 * Named exports, one per idea, so a module that draws two ideas bundles two
 * icons, not the table.
 */
import {
  Archive,
  ArrowLeftRight,
  ArrowRightLeft,
  Award,
  Banknote,
  Bath,
  BedDouble,
  BetweenVerticalStart,
  BookOpen,
  BookOpenText,
  CalendarCheck,
  CalendarClock,
  Cast,
  ChartNoAxesColumn,
  CircleDollarSign,
  CircleX,
  ClipboardCheck,
  ClipboardPenLine,
  ShieldCheck,
  Clock,
  ClockFading,
  Cog,
  DraftingCompass,
  File,
  FileDiff,
  FilePenLine,
  FileText,
  Files,
  Folder,
  Forward,
  GalleryVerticalEnd,
  HandCoins,
  Handshake,
  Highlighter,
  HousePlus,
  Houses,
  IdCard,
  LandPlot,
  Landmark,
  Languages,
  LayoutDashboard,
  LayoutList,
  Lightbulb,
  Lock,
  MailCheck,
  MapPinHouse,
  MapPinned,
  Mic,
  MonitorSmartphone,
  PackagePlus,
  PenLine,
  Presentation,
  Ruler,
  ScanLine,
  Smartphone,
  SquareDimensions,
  Store,
  SwatchBook,
  Tablet,
  TabletSmartphone,
  Tags,
  Timer,
  UserGroup,
  UserPen,
  Users,
  Wallet,
  Workflow,
  Activity,
  Ban,
  CalendarX,
  CircleArrowUp,
  Download,
  FileClock,
  FileLock,
  FileSpreadsheet,
  LayoutPanelLeft,
  Library,
  Megaphone,
  UserRoundCheck,
  ShieldUser,
  type LucideIcon,
} from "lucide-react";

/** Site plan — `land-plot`. */
export const SitePlanIcon: LucideIcon = LandPlot;
/** Neighbourhood / area — `map-pinned`. */
export const NeighbourhoodIcon: LucideIcon = MapPinned;
/** Homes that fit a lot, lots that fit a home — `house-plus`. */
export const HomesThatFitIcon: LucideIcon = HousePlus;
/** Lot type / frontage — `square-dimensions`. */
export const LotTypeIcon: LucideIcon = SquareDimensions;
/** Lot premium — `tags`. */
export const LotPremiumIcon: LucideIcon = Tags;
/** Compare — `between-vertical-start`. */
export const CompareIcon: LucideIcon = BetweenVerticalStart;
/** Admin / staff settings door — `cog`. */
export const AdminSettingsIcon: LucideIcon = Cog;
/** Lock Admin — `lock`. */
export const LockAdminIcon: LucideIcon = Lock;
/** A visitor (self-serve) screen — `presentation`. */
export const VisitorScreenIcon: LucideIcon = Presentation;
/** A sales rep's screen or device — `tablet-smartphone`. */
export const RepScreenIcon: LucideIcon = TabletSmartphone;
/** A presentation screen / showing on the TV — `cast`. */
export const CastIcon: LucideIcon = Cast;
/** Livvy, the home finder — `mic`. */
export const LivvyIcon: LucideIcon = Mic;
/** A Company project — `store`. */
export const CompanyProjectIcon: LucideIcon = Store;
/** Home type — `houses`. */
export const HomeTypeIcon: LucideIcon = Houses;
/** Budget / price ceiling — `wallet`. */
export const BudgetIcon: LucideIcon = Wallet;
/** Home size (sq ft) — `ruler`. */
export const HomeSizeIcon: LucideIcon = Ruler;
/** Bedrooms — `bed-double`. */
export const BedroomsIcon: LucideIcon = BedDouble;
/** Bathrooms — `bath`. */
export const BathroomsIcon: LucideIcon = Bath;
/** Lot features — `square-dimensions`. */
export const LotFeaturesIcon: LucideIcon = SquareDimensions;
/** Closing date — `calendar-clock`. */
export const ClosingDateIcon: LucideIcon = CalendarClock;
/** A hold request — `timer`. */
export const HoldRequestIcon: LucideIcon = Timer;
/** A hold that expired — `clock`. */
export const HoldExpiredIcon: LucideIcon = Clock;
/** Home designs (tab) — `layout-dashboard`. */
export const HomeDesignsIcon: LucideIcon = LayoutDashboard;
/** Media (tab) — `gallery-vertical-end`. */
export const MediaIcon: LucideIcon = GalleryVerticalEnd;
/** Brochure — `book-open`. */
export const BrochureIcon: LucideIcon = BookOpen;
/** A screen in general — `monitor-smartphone`. */
export const ScreenIcon: LucideIcon = MonitorSmartphone;
/** Staff signed in on a screen — `tablet`. */
export const StaffSignedInIcon: LucideIcon = Tablet;
/** A plan request (emailed floor plan) — `file-text`. */
export const PlanRequestIcon: LucideIcon = FileText;
/** Send feedback — `lightbulb`. */
export const FeedbackIcon: LucideIcon = Lightbulb;
/** Language — `languages`. */
export const LanguageIcon: LucideIcon = Languages;
/** Settings › Queue — `timer`. */
export const QueueIcon: LucideIcon = Timer;
/** Leads — `users`. */
export const LeadsIcon: LucideIcon = Users;
/** Stats / analytics — `chart-no-axes-column`. */
export const StatsIcon: LucideIcon = ChartNoAxesColumn;
/** Settings › Plans — `layout-dashboard`. */
export const PlansSettingsIcon: LucideIcon = LayoutDashboard;
/** Settings › Setup — `monitor-smartphone`. */
export const SetupSettingsIcon: LucideIcon = MonitorSmartphone;
/** Scan this QR code — `scan-line`. */
export const ScanQrIcon: LucideIcon = ScanLine;
/** Community places on the area map — `landmark`. */
export const CommunityPlaceIcon: LucideIcon = Landmark;
/** SaleFish Partner — `handshake`. */
export const PartnerIcon: LucideIcon = Handshake;
/** Founding Client — `award`. */
export const FoundingClientIcon: LucideIcon = Award;
/** Agent Portal — `user-group`. */
export const AgentPortalIcon: LucideIcon = UserGroup;
/** Showroom on the buyer's own phone — `smartphone`. */
export const ShowroomPhoneIcon: LucideIcon = Smartphone;
/** Offer a worksheet a different unit — `arrow-right-left`. */
export const OfferDifferentUnitIcon: LucideIcon = ArrowRightLeft;
/** A worksheet's category — `folder`. */
export const WorksheetCategoryIcon: LucideIcon = Folder;
/** Write up the sale from an approved worksheet — `file-pen-line`. */
export const WriteUpSaleIcon: LucideIcon = FilePenLine;
/** Archive — `archive`. */
export const ArchiveIcon: LucideIcon = Archive;
/** Sales (the one Sales menu item) — `layout-list`. */
export const SalesIcon: LucideIcon = LayoutList;
/** A contract document — `file`. */
export const ContractDocumentIcon: LucideIcon = File;
/** A package / an envelope — `files`. */
export const EnvelopeIcon: LucideIcon = Files;
/** Signing — `pen-line`. */
export const SigningIcon: LucideIcon = PenLine;
/** A deposit or payment — `banknote`. */
export const DepositIcon: LucideIcon = Banknote;
/** An amendment — `file-diff`. */
export const AmendmentIcon: LucideIcon = FileDiff;
/** An assignment — `forward`. */
export const AssignmentIcon: LucideIcon = Forward;
/** A cancellation — `circle-x`. */
export const CancellationIcon: LucideIcon = CircleX;
/** A cooling-off period / rescission clock — `clock-fading`. */
export const CoolingOffIcon: LucideIcon = ClockFading;
/** Identity verification — `id-card`. */
export const IdentityIcon: LucideIcon = IdCard;
/** A signing officer — `user-pen`. */
export const SigningOfficerIcon: LucideIcon = UserPen;
/** Clause library — `book-open-text`. */
export const ClauseLibraryIcon: LucideIcon = BookOpenText;
/** A price change — `circle-dollar-sign`. */
export const PriceChangeIcon: LucideIcon = CircleDollarSign;
/** Change unit — `arrow-left-right`. */
export const ChangeUnitIcon: LucideIcon = ArrowLeftRight;
/** Siting — `map-pin-house`. */
export const SitingIcon: LucideIcon = MapPinHouse;
/** Architectural controls — `drafting-compass`. */
export const ArchitecturalControlsIcon: LucideIcon = DraftingCompass;
/** A colour package — `swatch-book`. */
export const ColourPackageIcon: LucideIcon = SwatchBook;
/** A markup — `highlighter`. */
export const MarkupIcon: LucideIcon = Highlighter;
/** A workflow — `workflow`. */
export const WorkflowIcon: LucideIcon = Workflow;
/** Email confirmation — `mail-check`. */
export const EmailConfirmationIcon: LucideIcon = MailCheck;
/** Options & upgrades — `package-plus`. */
export const OptionsUpgradesIcon: LucideIcon = PackagePlus;
/** Commission — `hand-coins`. */
export const CommissionIcon: LucideIcon = HandCoins;
/** Agreement date — `calendar-check`. */
export const AgreementDateIcon: LucideIcon = CalendarCheck;
/** A FINTRAC information record — `clipboard-check`. */
export const FintracRecordIcon: LucideIcon = ClipboardCheck;
/** A worksheet — `clipboard-pen-line`. */
export const WorksheetIcon: LucideIcon = ClipboardPenLine;
/** Verify a document (check a signed PDF against its signing record) — `shield-check`. */
export const VerifyDocumentIcon: LucideIcon = ShieldCheck;

/** Conditional (a sale's or unit's status) — `file-clock`. */
export const ConditionalIcon: LucideIcon = FileClock;
/** Expired (an offer, worksheet or quote) — `calendar-x`. */
export const ExpiredIcon: LucideIcon = CalendarX;
/** Sold (a sale's or unit's status) — `file-lock`. */
export const SoldIcon: LucideIcon = FileLock;
/** Offers (the menu item and page) — `clipboard-pen-line`, the same as a worksheet. */
export const OffersIcon: LucideIcon = ClipboardPenLine;
/** Configure widgets — `layout-panel-left`. */
export const ConfigureWidgetsIcon: LucideIcon = LayoutPanelLeft;
/** A system upgrade (the bell row, the release notes entry) — `circle-arrow-up`. */
export const SystemUpgradeIcon: LucideIcon = CircleArrowUp;
/** Export (any CSV or file export) — `download`. */
export const ExportIcon: LucideIcon = Download;
/** Import (any spreadsheet import) — `file-spreadsheet`. */
export const ImportIcon: LucideIcon = FileSpreadsheet;
/** A declined request (hold, worksheet, offer request) — `ban`. */
export const DeclinedIcon: LucideIcon = Ban;
/** Roles (Platform Settings) — `shield-user`. */
export const RolesIcon: LucideIcon = ShieldUser;
/** Usage (Platform Settings) — `activity`. */
export const UsageIcon: LucideIcon = Activity;
/** Document Library — `library`. */
export const DocumentLibraryIcon: LucideIcon = Library;
/** Launch event (an activity on a contact's timeline) — `megaphone`. */
export const LaunchEventIcon: LucideIcon = Megaphone;
/** Registered (a buyer-journey stage: signed up) — `user-round-check`. */
export const RegisteredIcon: LucideIcon = UserRoundCheck;

/**
 * The AI helpers (Fill automatically, Find fields, Make the rule) draw NO icon
 * (Andrew, 2026-10-27: a wand is decoration, it stands for nothing). Their
 * buttons still read it from here — `{AiAssistIcon ? <AiAssistIcon /> : null}` —
 * so giving them one later is this one line.
 */
export const AiAssistIcon = null as LucideIcon | null;
